import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import { zb, fileUrl, BUSY, STATUS_LABELS, KIND_LABELS, when } from "./api";
import styles from "./ZohoBilling.module.css";

export function StatusChip({ status }) {
  const tone =
    {
      posted: "good",
      done: "good",
      previewed: "info",
      no_changes: "muted",
      needs_mapping: "warn",
      posted_with_warnings: "warn",
      plan_changed: "warn",
      interrupted: "warn",
      failed: "bad",
      cancelled: "muted",
    }[status] || (BUSY.includes(status) ? "busy" : "muted");
  return (
    <span className={`${styles.status} ${styles[`status_${tone}`]}`}>
      {BUSY.includes(status) && <CircularProgress size={10} thickness={6} color="inherit" />}
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// Live writes to Zoho always go through this: it says exactly what will
// happen and needs a second click.
export function ConfirmDialog({ open, title, children, confirmLabel, onConfirm, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * One job: status, actions, the kind-specific preview/result, and the live log.
 *
 * props:
 *   runId
 *   onChanged()                 refresh the parent's list after a status change
 *   canPreview(run) / previewLabel
 *   post: (run) => null | { label, title, body }   when posting is allowed
 *   children(run, { refresh })  renders the preview / result
 */
export default function RunPanel({ runId, onChanged, canPreview, previewLabel = "Run preview", post, children }) {
  const [run, setRun] = useState(null);
  const [mappingChanged, setMappingChanged] = useState(false);
  const [log, setLog] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [acting, setActing] = useState(false);
  const offsetRef = useRef(0);
  const logRef = useRef(null);
  const polling = useRef(false);
  const mounted = useRef(true);

  // Appends only the new part of the log, so it must never run twice at once.
  const load = useCallback(async () => {
    const data = await zb(`/runs/${runId}?offset=${offsetRef.current}`);
    offsetRef.current = data.log.nextOffset;
    if (data.log.text) setLog((prev) => prev + data.log.text);
    setRun(data.run);
    setMappingChanged(data.mappingChanged);
    return data.run;
  }, [runId]);

  // One loop per panel: keeps loading every 2s while the job is busy and
  // tells the parent once it settles. Parents mount one panel per run
  // (key={runId}), so runId never changes under a running loop.
  const poll = useCallback(async () => {
    if (polling.current) return;
    polling.current = true;
    let sawBusy = false;
    try {
      while (mounted.current) {
        const r = await load();
        if (!BUSY.includes(r.status)) break;
        sawBusy = true;
        setShowLog(true);
        await new Promise((res) => setTimeout(res, 2000));
      }
      if (sawBusy && mounted.current && onChanged) onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      polling.current = false;
    }
  }, [load, onChanged]);

  useEffect(() => {
    mounted.current = true;
    poll();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log, showLog]);

  const act = async (path, okMsg) => {
    setActing(true);
    try {
      await zb(`/runs/${runId}/${path}`, { method: "POST" });
      if (okMsg) toast.info(okMsg);
      setShowLog(true);
      if (onChanged) onChanged();
      poll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActing(false);
    }
  };

  if (!run) {
    return (
      <div className={styles.card}>
        <CircularProgress size={20} />
      </div>
    );
  }

  const busy = BUSY.includes(run.status);
  const postInfo = !busy && run.status === "previewed" && !mappingChanged && post ? post(run) : null;
  const previewAllowed = !busy && canPreview && canPreview(run);

  return (
    <div className={styles.card}>
      <div className={styles.runHead}>
        <div>
          <div className={styles.runTitle}>
            {KIND_LABELS[run.kind]} #{run.id} <StatusChip status={run.status} />
          </div>
          <div className={styles.muted}>
            {run.input?.files?.length
              ? `${run.input.files.map((f) => f.name).slice(0, 3).join(", ")}${run.input.files.length > 3 ? ` +${run.input.files.length - 3} more` : ""} · `
              : ""}
            by {run.created_by || "—"} · {when(run.createdAt)}
            {run.previewed_at ? ` · previewed ${when(run.previewed_at)}` : ""}
            {run.posted_at ? ` · posted ${when(run.posted_at)} by ${run.posted_by || "—"}` : ""}
          </div>
        </div>
        <div className={styles.actions}>
          {busy && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={acting}
              onClick={() => act("cancel", "Cancelling…")}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          )}
          {previewAllowed && (
            <Button
              size="small"
              variant="outlined"
              disabled={acting}
              onClick={() => act("preview", "Preview started — nothing is posted")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {["uploaded"].includes(run.status) ? previewLabel : "Preview again"}
            </Button>
          )}
          {postInfo && (
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={acting}
              onClick={() => setConfirming(true)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {postInfo.label}
            </Button>
          )}
        </div>
      </div>

      {mappingChanged && run.status === "previewed" && (
        <div className={styles.warnBox}>
          The SKU mapping changed after this preview. Preview again before posting.
        </div>
      )}
      {run.error_message && <div className={styles.errorBox}>{run.error_message}</div>}

      {children && children(run, { refresh: poll })}

      <div className={styles.logBar}>
        <button type="button" className={styles.linkBtn} onClick={() => setShowLog((v) => !v)}>
          {showLog ? "Hide log" : "Show log"}
        </button>
        <a className={styles.linkBtn} href={fileUrl(run.id, "log.txt")}>
          Download log
        </a>
      </div>
      {showLog && (
        <pre ref={logRef} className={styles.log}>
          {log || "No output yet."}
        </pre>
      )}

      {postInfo && (
        <ConfirmDialog
          open={confirming}
          title={postInfo.title}
          confirmLabel={postInfo.label}
          onClose={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            act("post", "Posting to Zoho started");
          }}
        >
          {postInfo.body}
        </ConfirmDialog>
      )}
    </div>
  );
}
